<style lang="scss">
  @use "../../../assets/css/design-system/colors";
  @use "../../../assets/css/design-system/utils";
  @use "../../../assets/css/design-system/mixins";

  .wrapper {
    all: unset;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    cursor: text;

    &.full-width {
      flex: 1;
      max-width: 100%;
    }

    .label-text {
      padding: 0 utils.in_rem(4px) utils.in_rem(6px) utils.in_rem(4px);

      &.large {
        @include mixins.h4();
      }

      &.small {
        @include mixins.caption();
      }
    }

    .input-with-chips {
      width: 100%;
      flex: 1;
      display: flex;
      gap: utils.in_rem(8px);
      align-items: center;
      flex-direction: row;
      overflow: hidden;
      transition: all 0.5s ease;

      border: 1px solid colors.get(interfaceSwGrey_10);

      &.locked {
        cursor: default;
        > input {
          cursor: default;
        }
      }

      &.disabled {
        border: 2px solid colors.get(interfaceDisabledGrey_5);

        background: colors.get(interfaceDisabledGrey_10);
        cursor: default;
        > input {
          cursor: default;
        }
      }

      // size classes
      &.large {
        padding: utils.in_rem(8px) utils.in_rem(12px);
        min-height: utils.in_rem(44px);
        border-radius: 10px;
      }
      &.small {
        padding: utils.in_rem(4px) utils.in_rem(12px);
        min-height: utils.in_rem(36px);
        border-radius: 8px;
      }
      &.xsmall {
        // NB not in use yet - exact padding unknown
        padding: utils.in_rem(4px) utils.in_rem(8px);
        min-height: utils.in_rem(32px);
        border-radius: 8px;
        > input {
          @include mixins.body3();
        }
      }

      &:hover:not(.disabled) {
        border: 1px solid colors.get(primary_10);
      }

      // manual focus state, because the focusable input is a child of this div
      &:focus-within {
        outline: 1px solid colors.get(primary_40);
        flex-wrap: wrap;
        overflow-x: auto; // allow horizontal scrolling for super long chips
      }

      // always expanded state - applies the same layout as focus-within
      &.always-expanded {
        flex-wrap: wrap;
        overflow-x: auto;

        &:not(:focus-within) input {
          display: none;
        }
      }

      &.error {
        outline: 1px solid colors.get(systemError_100);
      }
      &.warning {
        outline: 1px solid colors.get(systemWarning_100);
      }

      .chip {
        flex-shrink: 0; // stop chips from changing size to fit in the box

        // Margin adjustments to stop the text area resizing when chips are added
        margin-top: utils.in_rem(-2px);
        margin-bottom: utils.in_rem(-1px);
      }

      input {
        flex: 1;
        border: none;
        outline: none;
        background-color: transparent;

        @include mixins.body2();
        @include colors.color(textBlack);
        &::placeholder {
          @include colors.color(textDisabledGrey);
        }
      }
    }

    .helper-text {
      display: flex;
      flex-direction: row;
      gap: utils.in_rem(4px);
      padding: utils.in_rem(6px) utils.in_rem(4px) 0 utils.in_rem(4px);
      @include colors.color(textGrey);
      @include mixins.caption();

      &.error {
        @include colors.color(systemError_100);
      }
      &.warning {
        @include colors.color(systemWarning_100);
      }
    }
  }

  .input-with-chips.truncate-content:not(:focus-within) {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    flex-wrap: nowrap;
  }

  .input-with-chips.truncate-content:not(:focus-within) .chip {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .input-with-chips.truncate-content:not(:focus-within) input {
    display: none;
  }
</style>

<script>
  import ActionChip from "../../atoms/ActionChip.svelte";
  import IconScale from "../../atoms/IconScale.svelte";

  let cls = "";
  export { cls as class };

  export let placeholder = "";
  export let label = "";
  export let labelSize = "small";
  export let helperText = "";
  export let state = "";

  export let size = "large"; // "large" | "small" | "xsmall"

  // Chips may be bound when using this component
  export let chips = [];
  export let useChipObjects = false; // If true, chips are objects with a 'label' property (allows other properties like icon, src, etc)
  export let chipAddPropsCallback = () => {}; // Optional function to add extra keys to a chip object when it's created

  // Invalid chips may be passed to highlight them (see chipKinds below)
  export let invalidChips = [];

  export let createChipKeydownEvents = ["Enter", ","]; // Keys that create a chip when pressed

  export let hidePlaceholderOnceChipsExist = false; // If true, placeholder will not be shown when chips exist
  export let blurOnChipCreation = false;
  export let blurOnChipCreationCallback = () => {};
  export let createChipOnBlur = true; // If true, a chip will be created when the input field loses focus
  export let removeChipCallback; // Callback function when a chip is removed - this is called *instead* of removeChip() - takes index as only argument
  export let leadingIcon = ""; // Icon to display on the left of the input field
  export let leadingSrc = ""; // Image URL to display on the left of the input field
  export let disabled = false; // If true, the input field will be disabled
  export let locked = false;
  export let showX = true; // If true, an 'x' button will be shown on each chip to remove it
  export let alwaysExpanded = false; // If true, chips will always be expanded (wrapped)
  export let defaultChipKind = "active"; // Kind of chips to use by default
  export let disableEdits = false; // If true, keydown events are ignored, as well as chip removal
  export let disableEditsCallback = () => {}; // Callback when an edit is attempted while disableEdits is true

  export let inputValue = ""; // allows parent component binding
  export let el; // allows parent component binding (e.g. for focus)

  let inputElement;
  // Prevent the textbox from scrolling when the input is focused
  export const focus = () => {
    if (disabled || locked) inputElement?.blur();
    else inputElement?.focus({ preventScroll: true });
  };

  const createChip = () => {
    if (inputValue.trim()) {
      const toAdd = useChipObjects
        ? { label: inputValue.trim(), ...chipAddPropsCallback(inputValue) }
        : inputValue.trim();
      chips = [...chips, toAdd];
      setTimeout(() => {
        inputValue = "";
      }); // clear input after chip is created so comma doesn't linger
    }
  };

  const removeChipInner = (index) => {
    chips = chips.filter((_, i) => i !== index);
  };

  const removeChip = (index) => {
    removeChipCallback ? removeChipCallback(index) : removeChipInner(index);
  };

  let backspaceCount = 0;

  const handleKeydown = (event) => {
    if (createChipKeydownEvents.includes(event.key)) {
      if (disableEdits) {
        disableEditsCallback();
        return;
      }

      createChip();
      backspaceCount = 0;
      if (blurOnChipCreation) {
        inputValue = "";
        inputElement.blur();
        blurOnChipCreationCallback();
      }
    } else if (event.key === "Backspace") {
      if (disableEdits) {
        disableEditsCallback();
        return;
      }

      if (inputValue === "" && chips.length > 0) {
        if (backspaceCount) {
          removeChip(chips.length - 1);
          backspaceCount = 0;
        } else {
          backspaceCount += 1;
        }
      }
    } else {
      backspaceCount = 0;
    }
  };

  const handleBlur = () => {
    backspaceCount = 0;
    if (createChipOnBlur) createChip();
  };

  // Indicate which chip is about to be deleted when backspace is pressed
  // Also highlight an errored chip
  let chipKinds = [];
  $: chipKinds = chips.map((chip, index) => {
    if (backspaceCount > 0 && inputValue === "" && index === chips.length - 1) {
      return "hover";
    }
    if (invalidChips.includes(useChipObjects ? chip.label : chip)) {
      return "error";
    }
    return defaultChipKind;
  });
  $: state = ["error", "warning"].find((x) => chipKinds.includes(x)) || "";
  $: inputPlaceholder =
    hidePlaceholderOnceChipsExist && chips.length > 0 ? "" : placeholder;
  $: chipSize = size === "xsmall" ? "xsmall" : "small";
</script>

<!-- <label> cannot be used with interactive chips, so we use <div> instead -->
<!-- A focus method here makes this div behave like <label> -->
<div
  class="{cls} wrapper"
  tabindex="-1"
  on:focus={focus}
  role="textbox"
  bind:this={el}
>
  {#if label}
    <span class="label-text {labelSize}">{label}</span>
  {/if}

  <!-- Setting a tabindex of -1 seems to allow the scrollbar to be used -->
  <!-- You can still tab into the input field within this div -->
  <!-- We need a focus method here if we want to focus the input when the div is focused -->
  <div
    class="input-with-chips {state} {size}"
    class:disabled
    class:locked
    class:always-expanded={alwaysExpanded}
    tabindex="-1"
    on:focus={focus}
  >
    {#each chips as chip, index}
      <div class="chip" role="button">
        <ActionChip
          kind={chipKinds[index]}
          size={chipSize}
          leadingIcon={useChipObjects ? chip.icon : leadingIcon}
          leadingSrc={useChipObjects ? chip.src : leadingSrc}
          {showX}
          xClick={() => {
            if (disableEdits) {
              disableEditsCallback();
              return;
            }
            removeChip(index);
            inputElement.focus();
          }}
        >
          {useChipObjects ? chip.label : chip}
        </ActionChip>
      </div>
    {/each}
    <input
      bind:this={inputElement}
      type="text"
      bind:value={inputValue}
      placeholder={inputPlaceholder}
      on:blur={handleBlur}
      on:keydown={handleKeydown}
      on:focus={() => {
        if (disabled || locked) inputElement.blur();
      }}
    />
  </div>

  {#if helperText}
    <span class="helper-text {state}">
      {#if state === "error" || state === "warning"}
        <IconScale class="fa-solid fa-circle-info" size="xs" />
      {/if}
      {helperText}
    </span>
  {/if}
</div>
